class DTInstagramCarousel {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-instagram-track]');
    this.items = Array.from(
      section.querySelectorAll('[data-instagram-item]')
    );

    this.prev = section.querySelector('[data-instagram-prev]');
    this.next = section.querySelector('[data-instagram-next]');
    this.progress = section.querySelector('[data-instagram-progress]');

    if (!this.track || this.items.length === 0) return;

    this.autoplay =
      section.dataset.autoplay === 'true';

    this.autoplaySpeed =
      parseInt(section.dataset.autoplaySpeed, 10) || 4000;

    this.loop =
      section.dataset.loop === 'true';

    this.pauseOnHover =
      section.dataset.pauseHover === 'true';

    this.timer = null;
    this.userInteracting = false;

    this.reducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }


  init() {
    this.bindEvents();

    this.updateControls();
    this.updateProgress();

    if (this.autoplay && !this.reducedMotion) {
      this.startAutoplay();
    }
  }


  bindEvents() {
    if (this.prev) {
      this.prev.addEventListener('click', () => {
        this.stopAutoplay();
        this.goPrevious();
        this.restartAutoplay();
      });
    }


    if (this.next) {
      this.next.addEventListener('click', () => {
        this.stopAutoplay();
        this.goNext();
        this.restartAutoplay();
      });
    }


    this.track.addEventListener(
      'scroll',
      () => {
        this.updateControls();
        this.updateProgress();
      },
      { passive: true }
    );


    this.track.addEventListener(
      'pointerdown',
      () => {
        this.userInteracting = true;
        this.stopAutoplay();
      },
      { passive: true }
    );


    window.addEventListener(
      'pointerup',
      () => {
        if (!this.userInteracting) return;

        this.userInteracting = false;

        this.restartAutoplay();
      },
      { passive: true }
    );


    this.track.addEventListener(
      'touchstart',
      () => {
        this.stopAutoplay();
      },
      { passive: true }
    );


    this.track.addEventListener(
      'touchend',
      () => {
        this.restartAutoplay();
      },
      { passive: true }
    );


    if (this.pauseOnHover) {
      this.section.addEventListener('mouseenter', () => {
        this.stopAutoplay();
      });

      this.section.addEventListener('mouseleave', () => {
        this.restartAutoplay();
      });
    }


    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.restartAutoplay();
      }
    });


    window.addEventListener(
      'resize',
      () => {
        this.updateControls();
        this.updateProgress();
      },
      { passive: true }
    );
  }


  getGap() {
    const style = getComputedStyle(this.track);

    return parseFloat(style.columnGap || style.gap) || 0;
  }


  getStep() {
    const firstItem = this.items[0];

    if (!firstItem) {
      return this.track.clientWidth;
    }

    return (
      firstItem.getBoundingClientRect().width +
      this.getGap()
    );
  }


  getMaxScroll() {
    return Math.max(
      0,
      this.track.scrollWidth - this.track.clientWidth
    );
  }


  isAtStart() {
    return this.track.scrollLeft <= 3;
  }


  isAtEnd() {
    return (
      this.track.scrollLeft >=
      this.getMaxScroll() - 3
    );
  }


  goNext() {
    if (this.isAtEnd()) {
      if (this.loop) {
        this.track.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      }

      return;
    }

    this.track.scrollBy({
      left: this.getStep(),
      behavior: 'smooth'
    });
  }


  goPrevious() {
    if (this.isAtStart()) {
      if (this.loop) {
        this.track.scrollTo({
          left: this.getMaxScroll(),
          behavior: 'smooth'
        });
      }

      return;
    }

    this.track.scrollBy({
      left: -this.getStep(),
      behavior: 'smooth'
    });
  }


  startAutoplay() {
    if (
      !this.autoplay ||
      this.reducedMotion ||
      this.items.length <= 1
    ) {
      return;
    }

    this.stopAutoplay();

    this.timer = window.setInterval(
      () => {
        this.goNext();
      },
      this.autoplaySpeed
    );
  }


  stopAutoplay() {
    if (!this.timer) return;

    clearInterval(this.timer);

    this.timer = null;
  }


  restartAutoplay() {
    if (
      !this.autoplay ||
      this.reducedMotion ||
      this.userInteracting
    ) {
      return;
    }

    this.startAutoplay();
  }


  updateControls() {
    if (!this.prev && !this.next) return;

    const noOverflow =
      this.track.scrollWidth <=
      this.track.clientWidth + 2;

    if (this.prev) {
      this.prev.classList.toggle(
        'is-disabled',
        noOverflow ||
        (!this.loop && this.isAtStart())
      );
    }


    if (this.next) {
      this.next.classList.toggle(
        'is-disabled',
        noOverflow ||
        (!this.loop && this.isAtEnd())
      );
    }
  }


  updateProgress() {
    if (!this.progress) return;

    const maxScroll = this.getMaxScroll();

    if (maxScroll <= 0) {
      this.progress.style.width = '100%';
      this.progress.style.transform = 'translateX(0)';
      return;
    }

    const trackWidth = this.track.clientWidth;
    const contentWidth = this.track.scrollWidth;

    const thumbRatio =
      Math.min(1, trackWidth / contentWidth);

    const thumbWidth =
      thumbRatio * 100;

    const scrollProgress =
      this.track.scrollLeft / maxScroll;

    const availableMovement =
      100 - thumbWidth;

    this.progress.style.width =
      `${thumbWidth}%`;

    this.progress.style.transform =
      `translateX(${scrollProgress * availableMovement / thumbRatio}%)`;
  }


  destroy() {
    this.stopAutoplay();
  }
}


function initDTInstagramCarousels(container = document) {
  container
    .querySelectorAll('[data-instagram-section]')
    .forEach(section => {

      if (section._dtInstagramCarousel) {
        return;
      }

      section._dtInstagramCarousel =
        new DTInstagramCarousel(section);
    });
}


document.addEventListener(
  'DOMContentLoaded',
  () => {
    initDTInstagramCarousels();
  }
);


document.addEventListener(
  'shopify:section:load',
  event => {
    initDTInstagramCarousels(event.target);
  }
);


document.addEventListener(
  'shopify:section:unload',
  event => {

    const section =
      event.target.querySelector(
        '[data-instagram-section]'
      );

    if (
      section &&
      section._dtInstagramCarousel
    ) {
      section._dtInstagramCarousel.destroy();
    }

  }
);
